/**
 * Decides whether a generic NetSuite record may be posted, and if not, why.
 *
 * This lives outside the review component on purpose. "May this document be
 * posted to a customer's live NetSuite account?" is the single most
 * consequential decision the review page makes, and it was previously an
 * inline boolean expression that no test could reach. Pulling it out means
 * the gate can be exercised directly, including the states that are awkward
 * to stage in a mounted component (validation in flight, backend
 * unreachable, a record already posted).
 *
 * Failure directions are deliberate and not symmetric:
 *   - a DEFINITE "invalid" from the backend blocks (fail closed)
 *   - an INABILITY to reach the backend does not block (fail open), because
 *     the post path runs the same validation server-side anyway, so a truly
 *     invalid payload is still refused there. Permanently disabling Post on
 *     a flaky network would strand the reviewer with no way forward.
 */

export const GATE_REASONS = {
  POSTING: 'posting',
  MISSING_REQUIRED: 'missing_required',
  NO_LINES: 'no_lines',
  CHECKING: 'checking',
  INVALID: 'invalid',
  ALREADY_POSTED: 'already_posted',
  READY: 'ready',
}

/**
 * @param {object}  args
 * @param {Array}   args.missingRequired  required header fields still empty
 * @param {boolean} args.posting          a post is already in flight
 * @param {boolean} args.hasLineItems     this record type carries lines
 * @param {number}  args.lineCount        usable line rows built from the doc
 * @param {object}  args.serverValidation { status, errors, alreadyPosted }
 * @returns {{canPost: boolean, reason: string, blockingErrors: Array}}
 */
export function computePostGate({
  missingRequired = [],
  posting = false,
  hasLineItems = false,
  lineCount = 0,
  serverValidation = { status: 'idle', errors: [] },
} = {}) {
  const errors = Array.isArray(serverValidation?.errors) ? serverValidation.errors : []
  const status = serverValidation?.status || 'idle'

  // Ordered most-specific-first so the reason shown to the reviewer is the
  // one they can actually act on next.
  if (posting) {
    return { canPost: false, reason: GATE_REASONS.POSTING, blockingErrors: [] }
  }
  if (serverValidation?.alreadyPosted) {
    return { canPost: false, reason: GATE_REASONS.ALREADY_POSTED, blockingErrors: [] }
  }
  if (missingRequired.length > 0) {
    return { canPost: false, reason: GATE_REASONS.MISSING_REQUIRED, blockingErrors: [] }
  }
  if (hasLineItems && lineCount <= 0) {
    return { canPost: false, reason: GATE_REASONS.NO_LINES, blockingErrors: [] }
  }
  if (status === 'checking') {
    return { canPost: false, reason: GATE_REASONS.CHECKING, blockingErrors: [] }
  }
  if (status === 'invalid') {
    return { canPost: false, reason: GATE_REASONS.INVALID, blockingErrors: errors }
  }
  // 'idle', 'valid' and 'unavailable' all reach here — see the fail-open
  // note above for why 'unavailable' is allowed through.
  return { canPost: true, reason: GATE_REASONS.READY, blockingErrors: [] }
}

/** Field keys the backend specifically complained about, for red borders. */
export function serverErrorFieldKeys(serverValidation) {
  const keys = new Set()
  const errors = Array.isArray(serverValidation?.errors) ? serverValidation.errors : []
  for (const err of errors) {
    if (err && err.field_key) keys.add(err.field_key)
  }
  return keys
}

/** Human-readable explanation for a disabled Post button. */
export function gateMessage(gate, missingRequired = []) {
  switch (gate?.reason) {
    case GATE_REASONS.POSTING:
      return 'Posting to NetSuite…'
    case GATE_REASONS.ALREADY_POSTED:
      return 'This record has already been posted to NetSuite'
    case GATE_REASONS.MISSING_REQUIRED: {
      const names = missingRequired.map(f => f?.label).filter(Boolean)
      if (names.length === 0) return 'Required fields are empty'
      if (names.length === 1) return `${names[0]} is required`
      return `${names.length} required fields are empty: ${names.slice(0, 3).join(', ')}${names.length > 3 ? '…' : ''}`
    }
    case GATE_REASONS.NO_LINES:
      return 'This record type needs at least one line item'
    case GATE_REASONS.CHECKING:
      return 'Checking with NetSuite…'
    case GATE_REASONS.INVALID:
      return 'NetSuite would reject this record'
    default:
      return ''
  }
}
